import { Box, Chip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../../global/theme/tokens';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { VacationTimeline } from './VacationTimeline';
import { hasPermissions } from '../../../auth/hasPermissions';
import moment from 'moment';
import { daysInYear } from './vacationUtils';

export const VacationRow = ({
  user,
  year,
  companyHolidays = [],
  onCreateNew,
  onEditVacation,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const name = `${user.lastName} ${user.firstName}`;
  const pending = user.vacationRequested - user.vacationTaken;
  const canManage = hasPermissions('employee-request-management', 'write');

  // double click on timeline to create new vacation request for that month
  const handleDoubleClick = (e) => {
    if (!canManage || !onCreateNew) return;
    // Calculate clicked date based on click position
    const rect = e.currentTarget.getBoundingClientRect();
    const fraction = Math.min(
      Math.max((e.clientX - rect.left) / rect.width, 0),
      0.9999,
    );
    // Get the corresponding day of the year and convert to date
    const dayIdx = Math.floor(fraction * daysInYear(year));
    const clicked = moment(`${year}-01-01`).add(dayIdx, 'days');

    // Call onCreateNew with the clicked month as start and end date
    onCreateNew(user, {
      startDate: clicked.clone().startOf('month').format('YYYY-MM-DD'),
      endDate: clicked.clone().startOf('month').format('YYYY-MM-DD'),
    });
  };

  return (
    <Box
      sx={{ borderBottom: `1px solid ${colors.selected}` }}
      className="@container flex items-stretch"
    >
      {/* Linke Info-Spalte - mobil nur Avatar, ab @md mit Text */}
      <Box className="flex items-center gap-3 py-2 px-2 @md:px-3 shrink-0 w-[56px] @md:w-[280px]">
        <Box
          onClick={() => navigate(`/employees/${user.id}`)}
          sx={{ cursor: 'pointer' }}
        >
          <ExoAvatar
            picture={user.picture && user.picture.temporaryUrl}
            name={name}
            size="40px"
          />
        </Box>
        <Box className="min-w-0 hidden @md:block">
          <Typography fontWeight={600} fontSize={14} noWrap>
            {name}
          </Typography>
          <Box className="flex items-center gap-1 flex-wrap">
            <Chip
              label={`${user.vacationTaken}/${user.vacationTotal}`}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                bgcolor: colors.success,
                color: '#fff',
              }}
            />
            {pending > 0 && (
              <Chip
                label={`${pending} ${t('Requested')}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  bgcolor: colors.warning,
                  color: '#fff',
                }}
              />
            )}
            {user.sickDaysTaken > 0 && (
              <Chip
                label={`${user.sickDaysTaken} ${t('Sick')}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  bgcolor: colors.error,
                  color: '#fff',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Rechte Timeline */}
      <Box
        className="flex-1 py-1"
        onDoubleClick={canManage ? handleDoubleClick : undefined}
        sx={{ cursor: canManage ? 'crosshair' : 'default' }}
      >
        <VacationTimeline
          sickDays={user.sickDays}
          year={year}
          companyHolidays={companyHolidays}
          onClickVacation={
            onEditVacation
              ? (range, anchor) => onEditVacation(user, range, anchor)
              : undefined
          }
          employeeRequests={user.employeeRequests}
        />
      </Box>
    </Box>
  );
};
